import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useParams } from 'react-router';
import { BookOpen, ChevronRight, LayoutList, Pencil, Trash2 } from 'lucide-react';
import { Badge } from '@repo/ui/src/components/ui/badge';
import { DropdownMenuItem } from '@repo/ui/src/components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@repo/ui/src/components/ui/alert-dialog';
import { useSubjects, useDeleteSubject, useSaveNoteTemplate } from '../../hooks/useSubjects';
import { useEnrollmentsForSubject } from '../../hooks/useEnrollmentsForSubject';
import { useCan } from '../../hooks/useCan';
import { PageHeader, type PageHeaderAction } from '../../components/PageHeader';
import { DetailSkeleton } from '../../components/DetailSkeleton';
import { EmptyState } from '../../components/EmptyState';
import { EditSubjectDialog } from './SubjectsPage';
import { NoteTemplateSection } from './NoteTemplateSection';

export function SubjectDetailPage() {
  const { t } = useTranslation('students');
  const { t: tCommon } = useTranslation('common');
  const { t: tProgress } = useTranslation('progress');
  const { id } = useParams();
  const navigate = useNavigate();
  const subjectId = id ?? null;
  // `useSubjects` fetches the whole list — there is no single-subject endpoint —
  // so "not found" must wait for the list, or a hard refresh flashes it.
  const { data: subjects, isLoading: isLoadingSubjects } = useSubjects();
  const subject = subjects?.find((s) => s.id === subjectId);
  const { data: enrollments, isLoading } = useEnrollmentsForSubject(subjectId);
  const { mutate: deleteSubject, isPending: isDeleting } = useDeleteSubject();
  const canManageSubjects = useCan('manage:subjects');
  // THI-129: same tuition gate as SubjectsPage's column — MEMBER keeps the
  // detail screen (attendance vocabulary) but not the rates.
  const canSeeMoney = useCan('manage:enrollments');
  const [editOpen, setEditOpen] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [removeTemplateOpen, setRemoveTemplateOpen] = useState(false);
  const saveTemplate = useSaveNoteTemplate(subjectId ?? '');

  if (isLoadingSubjects) return <DetailSkeleton />;

  if (!subject) {
    return (
      <div className="mx-auto w-full max-w-4xl p-8">
        <PageHeader
          size="md"
          back={{ label: t('detail.back'), onClick: () => navigate(-1) }}
          title={t('subjects.detail.notFound')}
        />
      </div>
    );
  }

  const rates = canSeeMoney
    ? ([
        subject.defaultRate != null && `${subject.defaultRate.toLocaleString('vi-VN')}${t('subjects.perSession')}`,
        subject.defaultMonthlyRate != null && `${subject.defaultMonthlyRate.toLocaleString('vi-VN')}${t('subjects.perMonth')}`,
      ].filter(Boolean) as string[])
    : [];
  const studentCount = enrollments?.length ?? 0;
  const adminOnly = canManageSubjects ? undefined : tCommon('rbac.adminOnly');

  const handleDelete = () =>
    deleteSubject(subject.id, {
      onSuccess: () => {
        setDeleteConfirmOpen(false);
        navigate('/subjects');
      },
    });

  // Same shape the list row's own ⋮ uses (SubjectsPage.tsx): "Sửa" through the
  // shared EditSubjectDialog, "Xoá" through the same confirm/handler, both
  // gated by manage:subjects — a subject has no owner-only distinction the
  // way a student/client record does, so both actions share one reason.
  const actions: PageHeaderAction[] = [
    {
      key: 'edit',
      label: tCommon('edit'),
      icon: Pencil,
      variant: 'outline',
      onClick: () => setEditOpen(true),
      disabledReason: adminOnly,
    },
    {
      key: 'delete',
      label: t('subjects.delete'),
      icon: Trash2,
      onClick: () => setDeleteConfirmOpen(true),
      disabled: isDeleting,
      destructive: true,
      disabledReason: adminOnly,
    },
  ];

  return (
    <div className="mx-auto w-full max-w-4xl p-8">
      <PageHeader
        size="md"
        back={{ label: t('detail.back'), onClick: () => navigate(-1) }}
        title={subject.name}
        badge={<Badge variant="muted">{t('nav.subjects')}</Badge>}
        actions={actions}
        // THI-193: "Bỏ sườn" sits in the ⋯ menu next to Xoá (handoff 19 §4),
        // never as an inline button (the header's third non-destructive action
        // would otherwise take the second inline slot). It only exists while a
        // template does and PUTs null after its own confirm.
        overflow={subject.noteTemplate ? (
          <DropdownMenuItem
            disabled={!canManageSubjects || saveTemplate.isPending}
            onSelect={() => setRemoveTemplateOpen(true)}
          >
            <LayoutList className="h-4 w-4" />
            {tProgress('template.remove')}
          </DropdownMenuItem>
        ) : undefined}
      />

      <EditSubjectDialog subject={subject} open={editOpen} onOpenChange={setEditOpen} />

      <AlertDialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('subjects.deleteConfirm.title')}</AlertDialogTitle>
            <AlertDialogDescription>{t('subjects.deleteConfirm.description')}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('subjects.deleteConfirm.cancel')}</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} disabled={isDeleting} className="bg-red-600 hover:bg-red-700">
              {t('subjects.deleteConfirm.confirm')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={removeTemplateOpen} onOpenChange={setRemoveTemplateOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{tProgress('template.removeTitle')}</AlertDialogTitle>
            <AlertDialogDescription>{tProgress('template.removeBody')}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{tProgress('template.removeCancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => saveTemplate.mutate(null, { onSuccess: () => setRemoveTemplateOpen(false) })}
              disabled={saveTemplate.isPending}
              className="bg-red-600 hover:bg-red-700"
            >
              {tProgress('template.removeConfirm')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Identity block: a subject has no actionable facts, only the default
          rates — one muted reference line, same slot the other two pages use. */}
      <div className="mb-[25.6px]">
        <p className="text-[13px] text-surface-400">
          {rates.length > 0 ? (
            <span className="tabular-nums">{rates.join(' · ')}</span>
          ) : (
            '—'
          )}
        </p>
      </div>

      <NoteTemplateSection subject={subject} />

      <section>
        <h2 className="mb-[9.6px] font-display text-[15px] font-semibold tracking-[-0.012em] text-surface-800">
          {t('subjects.detail.students')}{' '}
          {studentCount > 0 && <span className="font-normal text-surface-400">({studentCount})</span>}
        </h2>
        {isLoading ? (
          <div className="rounded-md border border-surface-200 bg-card px-4">
            {Array.from({ length: 3 }).map((_, i) => (
              <div key={i} className="border-b border-surface-100 py-[12.8px] last:border-b-0">
                <div className="h-4 w-40 animate-pulse rounded bg-primary/10" />
              </div>
            ))}
          </div>
        ) : studentCount === 0 ? (
          <div className="rounded-md border border-surface-200 bg-card">
            <EmptyState
              kind="teach"
              icon={BookOpen}
              title={t('subjects.detail.empty')}
              body={t('subjects.detail.emptyBody')}
              // No CTA: an enrollment is created from a student, not from here,
              // and a button that just moves you to a list is not an action.
            />
          </div>
        ) : (
          <div className="overflow-hidden rounded-md border border-surface-200 bg-surface-0">
            {enrollments!.map((e) => (
              <Link
                key={e.id}
                to={`/students/${e.student.id}`}
                className="flex items-center justify-between gap-[9.6px] border-b border-surface-100 px-4 py-[12.8px] last:border-b-0 hover:bg-surface-50"
              >
                <span className="text-sm font-medium text-surface-800">
                  {e.student.name}
                  {e.student.nickname && (
                    <span className="font-normal text-surface-400"> ({e.student.nickname})</span>
                  )}
                </span>
                <ChevronRight className="h-[14.4px] w-[14.4px] shrink-0 text-surface-400" strokeWidth={1.8} />
              </Link>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
